import { PropsWithChildren } from 'react';
import { WizardContext } from './WizardContext';
import { WizardConfig } from './types';

/**
 * Acting as the dependency injection layer, WizardProvider provides the Wizard context to its children, allowing them to access the config and read-only data.
 *
 * @summary
 * WizardProvider is a React context provider component that supplies the wizard configuration
 * and read-only data to its descendant components via the WizardContext. This enables dynamic logic within a wizard flow.
 *
 * @param children - The child React nodes that will have access to the Wizard context.
 * @param config - The configuration object containing components, resolver functions for navigation, and validation.
 * @param onAfterSubmit - Optional callback invoked after the wizard is submitted.
 * @param onCancel - Optional callback invoked when the wizard is cancelled.
 */
interface WizardProviderProps extends PropsWithChildren {
    config: WizardConfig;
    onAfterSubmit?: () => void;
    onCancel?: () => void;
}

export const WizardProvider = ({ config, onAfterSubmit, onCancel, children }: WizardProviderProps) => {
    const value = {
        config,
        onAfterSubmit,
        onCancel,
    };
    return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
};
